import { canonicalProcedureSlug } from '../applicant/utils/procedureOrganizer';
import { mvpApi } from '../services/api';
import { iso2FromCountryName } from '../utils/countries';
import {
  getCrewParticipantContext,
  getParticipantProjectName,
  persistParticipantProjectName,
} from '../utils/participantCrewContext';
import type { Answers } from './lib/form-model';

export type ParticipantAccount = {
  firstName: string;
  fullName: string;
  email: string;
  phone: string;
  initials: string;
  givenNames: string;
  familyName: string;
  projectName: string;
  jobTitle: string;
  procedureSlug: string;
  experienceInputs: Record<string, unknown>;
};

const SUPPORTED_DESTINATIONS = new Set(['US', 'ES', 'IT']);

function readStoredUser(): Record<string, unknown> | null {
  try {
    const raw = localStorage.getItem('userData');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function iso2(raw: unknown): string {
  const value = String(raw || '').trim();
  if (/^[A-Za-z]{2}$/.test(value)) return value.toUpperCase() === 'UK' ? 'GB' : value.toUpperCase();
  const fromName = iso2FromCountryName(value);
  return fromName && fromName !== '-' ? String(fromName).toUpperCase() : '';
}

function destinationFromSlug(slug: unknown): string {
  const match = String(slug || '').trim().match(/^([A-Za-z]{2})[-_]/);
  const code = match ? match[1].toUpperCase() : '';
  return SUPPORTED_DESTINATIONS.has(code) ? code : '';
}

function stayDays(start: unknown, end: unknown): number | undefined {
  const from = Date.parse(String(start || ''));
  const to = Date.parse(String(end || ''));
  if (!Number.isFinite(from) || !Number.isFinite(to) || to < from) return undefined;
  return Math.round((to - from) / 86400000) + 1;
}

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
}

export function accountFieldSeeds(account: ParticipantAccount): Answers {
  const seeds: Answers = {};
  if (account.givenNames) seeds['pf.identity.given_names'] = account.givenNames;
  if (account.familyName) seeds['pf.identity.family_primary'] = account.familyName;
  if (account.email) seeds['pf.contact.email'] = account.email;
  if (account.phone) seeds['pf.contact.phone'] = account.phone;
  if (account.jobTitle) seeds['pf.engagement.your_role'] = account.jobTitle;
  return seeds;
}

export async function loadParticipantAccount(): Promise<ParticipantAccount> {
  const crew = getCrewParticipantContext();
  let user = readStoredUser() || {};
  try {
    const res = await mvpApi.getMe();
    const next = res?.data?.user || res?.data;
    if (next && typeof next === 'object') {
      user = next;
      try {
        localStorage.setItem('userData', JSON.stringify(next));
      } catch {
        /* ignore quota */
      }
    }
  } catch {
    /* keep the stored session user */
  }

  const givenNames = String(user.first_name || '').trim();
  const familyName = String(user.last_name || '').trim();
  const crewName = String(crew?.name || '').trim();
  const fullName = [givenNames, familyName].filter(Boolean).join(' ') || crewName;
  const firstName = givenNames || crewName.split(/\s+/).filter(Boolean)[0] || '';
  const projectName = String(user.project_name || getParticipantProjectName() || crew?.projectName || '').trim();
  if (projectName) persistParticipantProjectName(projectName);
  const jobTitle = String(user.job_title || crew?.jobTitle || '').trim();

  const passport = iso2(user.country_of_origin || user.nationality || crew?.country);
  const destination = destinationFromSlug(user.procedure_slug || crew?.procedureSlug);
  const days = stayDays(crew?.scheduleStart, crew?.scheduleEnd);
  const experienceInputs: Record<string, unknown> = {
    production_kind: 'film_television',
    accompanies_principal_o1: false,
    employment_relationship: 'subordinate_employment',
  };
  if (passport) experienceInputs.passport_country = passport;
  if (destination) experienceInputs.destination_country = destination;
  if (days) experienceInputs.stay_days = days;

  return {
    firstName,
    fullName,
    email: String(user.email || crew?.email || '').trim(),
    phone: String(user.phone || crew?.phone || '').trim(),
    initials: initialsFromName(fullName),
    givenNames,
    familyName,
    projectName,
    jobTitle,
    procedureSlug: canonicalProcedureSlug(user.procedure_slug || crew?.procedureSlug),
    experienceInputs,
  };
}
